import isEmpty from 'lodash/isEmpty';

import isDefined from 'shared/utils/isDefined';
import { IPartialOrder } from 'checkout/types/Order';
import { PaymentVendor, PaymentVendorPriceRange } from 'checkout/enums/PaymentVendor';
import { IPaymentMethod } from 'checkout/types/PaymentMethod';

type IGetKlarnaEnabledCountry = (countryCode: string) => {
  currency: string;
  min: number;
  max: number;
  region: string;
};

export const isAllCredits = (order: IPartialOrder) =>
  !!order?.creditCents && !order?.finalPriceCents;
export const isAllGiftCards = (order: IPartialOrder) =>
  !isEmpty(order?.giftCards) && !order?.finalPriceCents;

/**
 * Here Be Dragons!
 *
 *         \****__              ____
 *         |    *****\_      --/ *\-__
 *         /_          (_    ./ ,/----'
 *            \__         (_./  /
 *             \__           \___----^__
 *               _/   _                  \
 *        |    _/  __/ )\"\ _____         *\
 *        |\__/   /    ^ ^       \____      )
 *         \___--"                    \_____ )
 *                                          "
 *
 * Just kidding.
 *
 * The following utils are used to determine whether to show a specific payment method, or if
 * the current order's default payment method from the backend is valid for the current order.
 *
 * This matrix is the source of truth for whether a payment method is valid for a given order.
 * https://docs.google.com/spreadsheets/d/18g4mnGrlmRa8fRK13MwUpI1zvPXM9fqsBin4yh4Krsc/edit#gid=0
 *
 * When deciding whether to show a method in the payment selector, there can be an additional modal
 * associated with the payment method that allows the user to switch currencies if the current one is not supported.
 */

export const isUSA = (countryCode: string) => {
  return countryCode === 'US';
};

export const isGermany = (countryCode: string) => {
  return countryCode === 'DE';
};

export const isUK = (countryCode: string) => {
  return countryCode === 'GB';
};

export const shouldShowAffirm = ({
  affirmAvailability,
  isPaymentAvailabilityServiceEnabled,
  isAffirmEnabled,
  isPurchasingGiftCard,
  isOffer,
  order,
  shippingCountryCode,
}: {
  affirmAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isAffirmEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isOffer: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
  shippingCountryCode: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(affirmAvailability);
  }

  return (
    isAffirmEnabled &&
    !isOffer &&
    !isPurchasingGiftCard &&
    isUSA(shippingCountryCode) &&
    !isAllCredits(order) &&
    !isAllGiftCards(order) &&
    order?.localizedFinalPriceCents?.amountUsdCents >=
      PaymentVendorPriceRange[PaymentVendor.Affirm].min
  );
};

export const shouldShowAfterpay = ({
  afterpayAvailability,
  isPaymentAvailabilityServiceEnabled,
  isAfterpayEnabled,
  isPurchasingGiftCard,
  isOffer,
  order,
  shippingCountryCode,
}: {
  afterpayAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isAfterpayEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isOffer: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
  shippingCountryCode: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(afterpayAvailability);
  }

  const finalPriceUsdCents = order?.localizedFinalPriceCents?.amountUsdCents;

  return (
    isAfterpayEnabled &&
    !isOffer &&
    !isPurchasingGiftCard &&
    isUSA(shippingCountryCode) &&
    !isAllCredits(order) &&
    !isAllGiftCards(order) &&
    finalPriceUsdCents >= PaymentVendorPriceRange[PaymentVendor.AfterPay].min &&
    finalPriceUsdCents <= PaymentVendorPriceRange[PaymentVendor.AfterPay].max
  );
};

export const shouldShowAlipay = ({
  alipayAvailability,
  isPaymentAvailabilityServiceEnabled,
  isAlipayEnabled,
  isPurchasingGiftCard,
  isOffer,
  order,
}: {
  alipayAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isAlipayEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isOffer: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(alipayAvailability);
  }

  return (
    isAlipayEnabled &&
    !isOffer &&
    !isPurchasingGiftCard &&
    !isAllCredits(order) &&
    !isAllGiftCards(order)
  );
};

export const shouldShowApplePay = ({
  applePayAvailable,
  order,
}: {
  applePayAvailable: boolean;
  order: IPartialOrder;
}) => {
  return applePayAvailable && !isAllCredits(order) && !isAllGiftCards(order);
};

export const shouldShowGiropay = ({
  giropayAvailability,
  isPaymentAvailabilityServiceEnabled,
  isPurchasingGiftCard,
  isGiropayEnabled,
  isOffer,
  order,
  shippingCountryCode,
  userRegionCountryCode,
}: {
  giropayAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isGiropayEnabled: boolean;
  isOffer: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
  shippingCountryCode: string;
  userRegionCountryCode: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(giropayAvailability);
  }

  return (
    !isPurchasingGiftCard &&
    isGiropayEnabled &&
    !isOffer &&
    (isGermany(shippingCountryCode) || isGermany(userRegionCountryCode)) &&
    !isAllCredits(order)
  );
};

const KLARNA_ENABLED_COUNTRIES = new Map([
  ['AT', { region: 'EU', min: 3500, max: 250000, currency: 'EUR' }],
  ['BE', { region: 'EU', min: 3500, max: 150000, currency: 'EUR' }],
  ['CA', { region: 'CA', min: 3500, max: 150000, currency: 'CAD' }], // Canada
  ['DE', { region: 'EU', min: 3500, max: 250000, currency: 'EUR' }],
  ['ES', { region: 'EU', min: 3500, max: 1000000, currency: 'EUR' }],
  ['FI', { region: 'EU', min: 3500, max: 300000, currency: 'EUR' }],
  ['FR', { region: 'EU', min: 3500, max: 150000, currency: 'EUR' }],
  ['GB', { region: 'EU', min: 3500, max: 100000, currency: 'EUR' }],
  ['GR', { region: 'EU', min: 3500, max: 100000, currency: 'EUR' }], // Greece
  ['IT', { region: 'EU', min: 3500, max: 100000, currency: 'EUR' }],
  ['IE', { region: 'EU', min: 3500, max: 100000, currency: 'EUR' }], // Ireland
  ['NL', { region: 'EU', min: 3500, max: 150000, currency: 'EUR' }],
  ['PT', { region: 'EU', min: 3500, max: 100000, currency: 'EUR' }], // Portugal
  ['UK', { region: 'UK', min: 3500, max: 100000, currency: 'GBP' }],
  ['US', { region: 'NA', min: 3500, max: 1000000, currency: 'USD' }],
]);

export const getKlarnaEnabledCountry: IGetKlarnaEnabledCountry = (countryCode) => {
  return KLARNA_ENABLED_COUNTRIES.get(countryCode);
};

export const shouldShowKlarna = ({
  klarnaAvailability,
  isPaymentAvailabilityServiceEnabled,
  isPurchasingGiftCard,
  isKlarnaEnabled,
  isOffer,
  order,
  shippingCountryCode,
  userRegionCountryCode,
}: {
  klarnaAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isKlarnaEnabled: boolean;
  isOffer: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
  shippingCountryCode: string;
  userRegionCountryCode: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(klarnaAvailability);
  }

  const { region, min, max } =
    getKlarnaEnabledCountry(userRegionCountryCode) ||
    getKlarnaEnabledCountry(shippingCountryCode) ||
    {};

  const isInAllowedCountry = !!region;

  const finalPriceUsdCents = order?.localizedFinalPriceCents?.amount;
  const isPriceAllowed = min < finalPriceUsdCents && finalPriceUsdCents < max;

  return (
    !isPurchasingGiftCard &&
    !isOffer &&
    isKlarnaEnabled &&
    isInAllowedCountry &&
    !isAllCredits(order) &&
    isPriceAllowed
  );
};

export const shouldShowPayPal = ({
  paypalAvailability,
  isPaymentAvailabilityServiceEnabled,
  isPurchasingGiftCard,
  isPayPalEnabled,
  order,
}: {
  paypalAvailability: IPaymentMethod;
  isPaymentAvailabilityServiceEnabled: boolean;
  isPurchasingGiftCard: boolean;
  isPayPalEnabled: boolean;
  order: IPartialOrder;
  selectedCurrencyCode?: string;
}) => {
  if (isPaymentAvailabilityServiceEnabled && !isPurchasingGiftCard) {
    return isDefined(paypalAvailability);
  }

  const isPriceGreaterThanMinAllowed =
    order?.localizedFinalPriceCents?.amountUsdCents >=
    PaymentVendorPriceRange[PaymentVendor.PayPal].min;

  return !isPurchasingGiftCard && isPayPalEnabled && isPriceGreaterThanMinAllowed;
};
